import { IRenderingCommonContext } from "@commonRendering/types";
import {
    IMenuWidgetData, INavigationLinkData, MenuLayoutType, MenuWidgetData, NavigationMenuType,
    Orientation
} from "@pageBuilder/typings";
import { IAnimatedWidget } from "../../../typings/widget-style-data";
import { LinkRenderer } from "../link.renderer";
import { WidgetRendererBase } from "../widget-renderer-base";
import { MenuWidgetScriptRenderer } from "./menu-widget-script.renderer";
import { MenuWidgetStyleRenderer } from "./menu-widget-style-renderer";
import { SubmenuStyleRenderer } from "./submenu-style-renderer";

export class MenuWidgetRenderer extends WidgetRendererBase<IMenuWidgetData> {

    private readonly menuStyleRenderer: MenuWidgetStyleRenderer;
    private readonly submenuStyleRenderer: SubmenuStyleRenderer;
    private readonly linkRenderer: LinkRenderer;
    private readonly scriptRenderer: MenuWidgetScriptRenderer;

    constructor(renderingCommonContext: IRenderingCommonContext) {
        super(renderingCommonContext);
        this.menuStyleRenderer = new MenuWidgetStyleRenderer(this.styleService);
        this.submenuStyleRenderer = new SubmenuStyleRenderer(this.styleService);
        this.menuStyleRenderer = new MenuWidgetStyleRenderer(this.styleService);
        this.linkRenderer = new LinkRenderer();
        this.scriptRenderer = new MenuWidgetScriptRenderer();
    }

    protected createNewInternal(): HTMLElement {
        return this.createWidgetElement("menuWidget");
    }

    protected renderInternal(element: HTMLElement, data: IMenuWidgetData): HTMLElement {
        const previousData = this.getData(element);

        if (previousData) {
            const previousLayoutType = previousData.menuStyle?.itemsLayoutType ?? "default";
            if (data.id && previousData.id === data.id) {
                this.menuStyleRenderer.clearStylesForLayout(element, previousLayoutType, data.menuStyle);
            }

            this.cleanupItems(element);
        }

        const content = element.querySelector(".widget-content") as HTMLElement;
        const editorLayout = this.getNavigation(data);

        content.appendChild(editorLayout);

        this.scriptRenderer.renderCommon(this.renderedDocument);
        this.renderStyles(element, data);

        if (data.layoutType === "more") {
            window["__truSites"]?.MenuWidgetInteraction?.processMenuWidget(element);
        }

        return element;
    }

    protected renderStyles(element: HTMLElement, data: IMenuWidgetData): void {
        this.renderBaseStyles(element, data.style);
        this.renderMenuStyles(element, data);
        this.renderSubmenuStyles(element, data);
    }

    private renderBaseStyles(element: HTMLElement, style: IAnimatedWidget): void {
        this.animationRenderer.setAnimationStyles(element, style.animation);
    }

    private renderMenuStyles(element: HTMLElement, data: IMenuWidgetData): void {
        if (data.menuStyle) {
            this.menuStyleRenderer.render(element, data.menuStyle, data.layoutType);
        }
    }

    private renderSubmenuStyles(element: HTMLElement, data: IMenuWidgetData): void {
        if (data?.submenuStyle) {
            this.submenuStyleRenderer.render(element, data.submenuStyle);
        }
    }

    private getNavigation(data: IMenuWidgetData): HTMLElement {
        const wrapper = document.createElement("div");
        const nav = document.createElement("nav");
        wrapper.classList.add("nav-wrapper");

        const ul = this.getEditorNavigationList(data);
        if (data.layoutType) {
            ul?.classList.add(data.layoutType?.toString());
        }

        if (data.menuStyle?.itemsLayoutType) {
            ul?.classList.add(data.menuStyle.itemsLayoutType?.toString());
        }

        nav.append(ul);
        wrapper.append(nav);
        return wrapper;
    }

    private renderOrientation(element: HTMLElement, data: IMenuWidgetData): void {
        if (data.menuStyle?.orientation === Orientation.Horizontal) {
            element.classList.add("horizontal");
        } else {
            element.classList.add("vertical");
        }
    }

    private getEditorNavigationList(data: IMenuWidgetData): HTMLElement {
        const ul = document.createElement("ul");
        ul.classList.add("root");
        this.renderOrientation(ul, data);
        ul.classList.toggle("custom", data.menuType === NavigationMenuType.Custom);
        ul.classList.toggle("gdpr-policy", data.menuType === NavigationMenuType.GdprPolicy);
        ul.classList.toggle("checkout-policy", data.menuType === NavigationMenuType.CheckoutPolicy);

        if (data.links) {
            data.links.forEach((link, index) => {
                const hasSublinks = !!link.sublinks && link.sublinks.length > 0;
                const addDivider = data.menuStyle?.itemsLayoutType === "divided" && index !== data.links.length - 1;
                const li = this.getNavigationItem(link, hasSublinks, true, addDivider);

                if (hasSublinks) {
                    const dropdown = this.createDropdown(link.sublinks);
                    li.append(dropdown);
                    li.classList.add("with-submenu");
                }
                if(data.menuStyle?.orientation === Orientation.Vertical){
                    li.style.alignSelf = data.menuStyle?.alignment as string;
                }
                ul.append(li);
            });

            if (data.layoutType === "more") {
                const moreBtnLi = this.getNavigationItem(MenuWidgetData.defaultMoreLinkData(this.renderingCommonContext.data.lang), true, false);
                moreBtnLi.classList.add("more");
                moreBtnLi.classList.add("with-submenu");
                const dropdown = this.createDropdown();
                moreBtnLi.append(dropdown);
                ul.append(moreBtnLi);
            }
        }

        return ul;
    }

    private createDropdown(links?: INavigationLinkData[]): HTMLElement {
        const dropdown = document.createElement("div");
        dropdown.classList.add("dropdown");
        const subUl = document.createElement("ul");
        subUl.classList.add("submenu");

        if (links) {
            links.forEach(sublink => {
                const subLi = this.getNavigationItem(sublink, false, true);
                subUl.append(subLi);
            });
        }

        dropdown.append(subUl);
        return dropdown;
    }

    private getNavigationItem(item: INavigationLinkData | null = null, includeChevron = true, includeHref = true, includeDivider = false): HTMLElement {
        const li = document.createElement("li");
        const span = document.createElement("span");
        span.classList.add("nav-item-name-label");

        const a = document.createElement("a") as HTMLAnchorElement;

        if (item) {
            a.id = item.id;
            span.innerHTML = item.text;
            if (includeHref) {
                this.linkRenderer.render(a, item.value);
            }

            a.classList.toggle("none-link", item.value.type === "none");
            a.setAttribute("tabindex", "-1");
            a.append(span);
        }

        a.classList.add("nav-item-name");

        if (includeChevron) {
            const chevron = this.createChevron();
            a.append(chevron);
        }

        if (includeDivider) {
            a.append(this.createDivider());
        }

        li.append(a);
        if (item?.isActive) {
            li.classList.toggle("active", true);
        }

        return li;
    }

    private createDivider(): HTMLElement {
        const span = document.createElement("span");
        span.classList.add("divider");
        return span;
    }

    private createChevron(): HTMLElement {
        const chevron = document.createElement("div");
        const chevronInner = document.createElement("div");
        chevron.classList.add("chevron");
        chevronInner.classList.add("inner");
        chevron.append(chevronInner);

        return chevron;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected cleanupInternal(element: HTMLElement): void {
        //nothing
    }

    protected getContentSelectorsInternal(element: HTMLElement): Array<string> {
        const result = [`#${element.id}`];
        result.push(...this.menuStyleRenderer.getStylesSelectors(element));
        result.push(...this.submenuStyleRenderer.getStylesSelectors(element.id));
        return result;
    }

    private cleanupItems(element: HTMLElement): void {
        const items = element.querySelectorAll(".nav-wrapper nav ul li");
        items?.forEach(li => {
            li.remove();
        });

        const navigations = element.querySelectorAll<HTMLAnchorElement>(".nav-wrapper");
        navigations.forEach(navigation => {
            navigation?.remove();
        });
    }

}
